import {
  OnboardingSaveCompanyPolicyRequest,
  OnboardingSavePublicTransportDto,
  OnboardingSaveHighwaysDto,
  OnboardingSaveParkingDto,
  OnboardingStep,
} from '@interfaces/models';
import {
  formatPublicTransportConfiguration,
  formatHighwaysConfiguration,
  formatParkingConfiguration,
} from '@shared/components';
import { getCompanyPolicyData, saveCompanyPolicy } from '@api/onboarding';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useGetCompanyPolicyData } from '@shared/hooks';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useMemo } from 'react';
import { Routes } from '@shared/enums';
import { toast } from '@ui/components';

export const useCompanyPolicyData = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data: companyPolicyData, isLoading: isQueryLoading } = useGetCompanyPolicyData();

  const { data: generalConfig, isLoading: isGeneralConfigLoading } = useQuery({
    queryKey: ['onboarding', 'company-policy'],
    queryFn: getCompanyPolicyData,
  });

  const [parkingConfiguration, setParkingConfiguration] = useState<OnboardingSaveParkingDto | null>(
    null,
  );
  const [highwaysConfiguration, setHighwaysConfiguration] =
    useState<OnboardingSaveHighwaysDto | null>(null);
  const [publicTransportConfiguration, setPublicTransportConfiguration] =
    useState<OnboardingSavePublicTransportDto | null>(null);

  const [isUnsavedChangesDialogOpen, setIsUnsavedChangesDialogOpen] = useState(false);
  const [isParkingConfigurationEditing, setIsParkingConfigurationEditing] = useState(false);
  const [isHighwaysConfigurationEditing, setIsHighwaysConfigurationEditing] = useState(false);
  const [isPublicTransportConfigurationEditing, setIsPublicTransportConfigurationEditing] =
    useState(false);

  const saveCompanyPolicyMutation = useMutation({
    mutationFn: (payload: OnboardingSaveCompanyPolicyRequest) => {
      return saveCompanyPolicy(payload);
    },
    onSuccess: () => {
      setIsUnsavedChangesDialogOpen(false);
      navigate(`../${Routes.FINISH}`);
    },
  });

  const saveCompanyPolicyConfiguration = ({ saveAnyway = false }: { saveAnyway?: boolean }) => {
    const hasAnyService =
      !!parkingConfiguration || !!highwaysConfiguration || !!publicTransportConfiguration;

    if (!hasAnyService) {
      toast.error(t('ONBOARDING.COMPANY_POLICY.AT_LEAST_ONE_SERVICE_REQUIRED'));
      return;
    }

    const isAnyConfigurationEditing =
      isPublicTransportConfigurationEditing ||
      isHighwaysConfigurationEditing ||
      isParkingConfigurationEditing;

    if (isAnyConfigurationEditing && !saveAnyway) {
      return setIsUnsavedChangesDialogOpen(true);
    }

    const payload: OnboardingSaveCompanyPolicyRequest = {
      highwaysConfiguration: formatHighwaysConfiguration(highwaysConfiguration),
      parkingConfiguration: formatParkingConfiguration(parkingConfiguration),
      publicTransportConfiguration: formatPublicTransportConfiguration(
        publicTransportConfiguration,
      ),
      nextStep: OnboardingStep.FINISH,
    };

    saveCompanyPolicyMutation.mutate(payload);
  };

  const availableServices = useMemo(() => {
    return generalConfig?.availableServices.map((service) => service.type) || [];
  }, [generalConfig]);

  return {
    // Configuration getters and setters
    parkingConfiguration,
    setParkingConfiguration,
    highwaysConfiguration,
    setHighwaysConfiguration,
    publicTransportConfiguration,
    setPublicTransportConfiguration,

    // Editing state getters and setters
    isParkingConfigurationEditing,
    setIsParkingConfigurationEditing,
    isHighwaysConfigurationEditing,
    setIsHighwaysConfigurationEditing,
    isPublicTransportConfigurationEditing,
    setIsPublicTransportConfigurationEditing,

    // Dialog state
    isUnsavedChangesDialogOpen,
    setIsUnsavedChangesDialogOpen,

    // API related
    isMutationPending: saveCompanyPolicyMutation.isPending,
    saveCompanyPolicyConfiguration,
    isGeneralConfigLoading,
    availableServices,
    companyPolicyData,
    isQueryLoading,
  };
};
